import { ref } from "vue";
import heic2any from "heic2any";

export const roleOptions = ref([
    { label: "Homeowner", value: "Homeowner" },
    { label: "Designer", value: "Designer" },
    { label: "Engineer", value: "Engineer" },
    { label: "Architect", value: "Architect" },
    { label: "Foreman", value: "Foreman" },
    { label: "Inspector", value: "Inspector" },
    { label: "Billing Manager", value: "Billing Manager" },
    { label: "Vendor", value: "Vendor" },
    { label: "Project Agent", value: "Project Agent" },
]);

// utils/customSort.js
export function customFilterAndSort(options, searchTerm) {
    if (!searchTerm) {
      // If no searchTerm, return all options as-is
      return [...options];
    }
  
    // First, filter items that "include" the search term
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = options.filter(option =>
      option.label.toLowerCase().includes(lowerSearch)
    );
  
    // Next, custom sort:
    //  1) items whose label starts with the searchTerm
    //  2) then items that only contain searchTerm
    //  3) each group sorted alphabetically
    filtered.sort((a, b) => {
      const aLabel = a.label.toLowerCase();
      const bLabel = b.label.toLowerCase();
  
      const aStarts = aLabel.startsWith(lowerSearch);
      const bStarts = bLabel.startsWith(lowerSearch);
  
      // If exactly one starts with the term, it goes first
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
  
      // Otherwise, both or neither start => sort by label
      return aLabel.localeCompare(bLabel);
    });
  
    return filtered;
  }
  

export const parseDate = (date) => {
    const parsedDate = new Date(date);
    // Check if the date is valid
    if (isNaN(parsedDate.getTime())) {
        return '...'; // Return empty string if the date is invalid
    }
    const options = {
        year: "numeric",
        month: "short",
        weekday: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
    };
    return parsedDate.toLocaleString(undefined, options);
};

export const convertHeicToPng = async (file) => {
  // Convert HEIC/HEIF to PNG using heic2any
  let convertedBlob = await heic2any({
    blob: file,
    toType: "image/png",
  });

  // Optional: Downscale if the converted image is larger than 10MB
  const maxInitialSize = 10 * 1024 * 1024; // 10MB
  if (convertedBlob.size > maxInitialSize) {
    console.log("Downscaling image from >10MB");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    convertedBlob = await new Promise((resolve, reject) => {
      img.onload = async () => {
        const scale = Math.sqrt(maxInitialSize / convertedBlob.size);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => resolve(blob), "image/png", 0.8);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(convertedBlob);
    });
  }

  // Now compress the PNG so that its size is at most 1MB
  const maxFinalSize = 1 * 1024 * 1024; // 1MB in bytes
  if (convertedBlob.size > maxFinalSize) {
    console.log("Compressing PNG to under 1MB");
    convertedBlob = await compressPng(convertedBlob, maxFinalSize);
  }
  // Create a named File from the blob
  const convertedFile = new File([convertedBlob], "converted_image.png", { type: "image/png" });
  return convertedFile;
};

async function compressPng(blob, maxSize) {
  let currentBlob = blob;
  // Loop: downscale 10% at a time until the size is below maxSize
  while (currentBlob.size > maxSize) {
    const img = new Image();
    img.src = URL.createObjectURL(currentBlob);
    await new Promise((resolve) => {
      img.onload = resolve;
    });
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = img.width * 0.9;
    canvas.height = img.height * 0.9;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    currentBlob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );
  }
  return currentBlob;
}


export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';

    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const unitIndex = Math.floor(Math.log(bytes) / Math.log(1024));

    return `${(bytes / Math.pow(1024, unitIndex)).toFixed(2)} ${units[unitIndex]}`;
};

